/* Il Botteghino — theme.js (no jQuery) */
(function(){
  'use strict';

  // =========================================================
  // NAMESPACE: window.Botteghino
  // Public API esposta per altri script + sezioni
  // =========================================================
  const Botteghino = window.Botteghino = window.Botteghino || {};

  // ---------- Modal API ----------
  const ModalAPI = (function(){
    const focusable = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
    let activeModal = null;
    let lastFocus = null;

    function open(id){
      const m = typeof id === 'string' ? document.getElementById(id) : id;
      if(!m || !m.matches('[data-modal]')) return;
      lastFocus = document.activeElement;
      m.hidden = false;
      document.body.classList.add('modal-open');
      activeModal = m;
      // Focus first focusable element
      requestAnimationFrame(() => {
        const f = m.querySelector(focusable);
        if(f) f.focus();
      });
      m.dispatchEvent(new CustomEvent('modal:open', { bubbles:true }));
    }

    function close(modalOrEl){
      const m = modalOrEl && modalOrEl.matches && modalOrEl.matches('[data-modal]')
        ? modalOrEl
        : (modalOrEl && modalOrEl.closest ? modalOrEl.closest('[data-modal]') : activeModal);
      if(!m) return;
      m.hidden = true;
      document.body.classList.remove('modal-open');
      if(activeModal === m) activeModal = null;
      if(lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
      m.dispatchEvent(new CustomEvent('modal:close', { bubbles:true }));
    }

    // Global click delegation
    document.addEventListener('click', e => {
      const openBtn = e.target.closest('[data-modal-open]');
      if(openBtn){
        e.preventDefault();
        open(openBtn.getAttribute('data-modal-open'));
        return;
      }
      const closeBtn = e.target.closest('[data-modal-close]');
      if(closeBtn){
        e.preventDefault();
        close(closeBtn);
      }
    });

    // Esc to close
    document.addEventListener('keydown', e => {
      if(e.key === 'Escape' && activeModal){
        close(activeModal);
      }
      // Focus trap
      if(e.key === 'Tab' && activeModal){
        const items = activeModal.querySelectorAll(focusable);
        if(items.length === 0) return;
        const first = items[0];
        const last = items[items.length - 1];
        if(e.shiftKey && document.activeElement === first){
          e.preventDefault(); last.focus();
        } else if(!e.shiftKey && document.activeElement === last){
          e.preventDefault(); first.focus();
        }
      }
    });

    return { open, close };
  })();
  Botteghino.modal = ModalAPI;

  // ---------- Toast API ----------
  const ToastAPI = (function(){
    function ensureContainer(){
      let c = document.getElementById('toast-container');
      if(!c){
        c = document.createElement('div');
        c.id = 'toast-container';
        c.className = 'toast-container';
        c.setAttribute('aria-live', 'polite');
        c.setAttribute('aria-atomic', 'false');
        document.body.appendChild(c);
      }
      return c;
    }

    function show(opts){
      const { message, type='info', ttl=4500 } = opts || {};
      if(!message) return;
      const c = ensureContainer();
      const t = document.createElement('div');
      t.className = `toast toast--${type}`;
      t.setAttribute('role', 'status');
      const icons = {
        success:'<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>',
        warning:'<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>',
        danger:'<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>',
        info:'<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>'
      };
      t.innerHTML = `<span class="toast-icon">${icons[type] || icons.info}</span><span class="toast-msg">${message}</span><button type="button" class="toast-close" aria-label="Chiudi"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>`;
      c.appendChild(t);
      const closeBtn = t.querySelector('.toast-close');
      const dismiss = () => {
        t.classList.add('is-leaving');
        t.addEventListener('animationend', () => t.remove(), { once:true });
      };
      closeBtn.addEventListener('click', dismiss);
      if(ttl > 0) setTimeout(dismiss, ttl);
      return t;
    }

    return {
      show,
      info:    msg => show({ message: msg, type: 'info' }),
      success: msg => show({ message: msg, type: 'success' }),
      warning: msg => show({ message: msg, type: 'warning' }),
      danger:  msg => show({ message: msg, type: 'danger' })
    };
  })();
  Botteghino.toast = ToastAPI;

  // ---------- Cookie Consent API (REQ-LEG-2) ----------
  // Storage key: 'botteghino_consent_v1' = { necessary:true, analytics:bool, marketing:bool, ts:int }
  const CONSENT_KEY = 'botteghino_consent_v1';
  const ConsentAPI = (function(){
    function read(){
      try { return JSON.parse(localStorage.getItem(CONSENT_KEY) || 'null'); }
      catch(e){ return null; }
    }
    function write(obj){
      try {
        const v = Object.assign({ necessary:true, analytics:false, marketing:false }, obj, { ts: Date.now() });
        localStorage.setItem(CONSENT_KEY, JSON.stringify(v));
        // Bridge to Shopify Customer Privacy API so Clarity/GA4/Pixels respect consent
        syncShopify(v);
        document.dispatchEvent(new CustomEvent('consent:updated', { detail: v, bubbles:true }));
        return v;
      } catch(e){ return null; }
    }
    function syncShopify(v){
      try {
        if(window.Shopify && window.Shopify.customerPrivacy && typeof window.Shopify.customerPrivacy.setTrackingConsent === 'function'){
          window.Shopify.customerPrivacy.setTrackingConsent({
            analytics: !!v.analytics,
            marketing: !!v.marketing,
            preferences: !!v.analytics,
            sale_of_data: !!v.marketing
          }, function(){ /* noop callback */ });
        }
      } catch(e){ /* swallow - non-critical */ }
    }
    function has(cat){
      const c = read();
      return !!(c && c[cat]);
    }
    function reset(){
      localStorage.removeItem(CONSENT_KEY);
      document.dispatchEvent(new CustomEvent('consent:reset', { bubbles:true }));
    }
    function accept(opts){
      // opts = { analytics:bool, marketing:bool }
      return write(opts || { analytics:true, marketing:true });
    }
    function reject(){
      return write({ analytics:false, marketing:false });
    }
    function showBanner(){
      const b = document.getElementById('cookie-banner');
      if(b){ b.hidden = false; b.setAttribute('aria-hidden','false'); }
    }
    function hideBanner(){
      const b = document.getElementById('cookie-banner');
      if(b){ b.hidden = true; b.setAttribute('aria-hidden','true'); }
    }
    return { read, write, has, reset, accept, reject, showBanner, hideBanner };
  })();
  Botteghino.consent = ConsentAPI;

  // Show cookie banner on first visit (run after DOM ready)
  // + bridge "Preferenze cookie" links (footer / pagine) to reopen the banner
  document.addEventListener('DOMContentLoaded', () => {
    if(!ConsentAPI.read()){
      ConsentAPI.showBanner();
    }
    document.querySelectorAll('[data-action="cookie-preferences"]').forEach(el => {
      el.addEventListener('click', e => {
        e.preventDefault();
        ConsentAPI.showBanner();
      });
    });
  });



  // === Mobile Nav ===
  const mobileToggle = document.querySelector('.mobile-toggle');
  const mobileNav = document.querySelector('.mobile-nav');
  if(mobileToggle && mobileNav){
    mobileToggle.addEventListener('click', () => mobileNav.classList.add('open'));
    mobileNav.querySelectorAll('[data-close-mn]').forEach(b =>
      b.addEventListener('click', () => mobileNav.classList.remove('open'))
    );
  }

  // === Drawer ===
  document.querySelectorAll('[data-open-drawer]').forEach(b =>
    b.addEventListener('click', e => {
      e.preventDefault();
      const id = b.getAttribute('data-open-drawer');
      const d = document.getElementById(id);
      if(d){ d.classList.add('open'); d.setAttribute('aria-hidden','false'); }
    })
  );
  document.querySelectorAll('[data-close-drawer]').forEach(b =>
    b.addEventListener('click', () => {
      const d = b.closest('.drawer');
      if(d){ d.classList.remove('open'); d.setAttribute('aria-hidden','true'); }
    })
  );

  // === PDP gallery ===
  const main = document.querySelector('.pdp-main img');
  document.querySelectorAll('.pdp-thumb').forEach(t =>
    t.addEventListener('click', () => {
      document.querySelectorAll('.pdp-thumb').forEach(x => x.classList.remove('active'));
      t.classList.add('active');
      const img = t.querySelector('img');
      if(main && img) main.src = img.src.replace('_small','_1024x1024').replace('_120x120','_1024x1024');
    })
  );

  // === Accordion ===
  document.querySelectorAll('.pdp-acc-head').forEach(h =>
    h.addEventListener('click', () => h.parentElement.classList.toggle('open'))
  );

  // === Qty ===
  document.querySelectorAll('.qty').forEach(q => {
    const minus = q.querySelector('[data-qty-minus]');
    const plus  = q.querySelector('[data-qty-plus]');
    const inp   = q.querySelector('input');
    if(!inp) return;
    minus && minus.addEventListener('click', () => { inp.value = Math.max(1,(parseInt(inp.value)||1)-1); inp.dispatchEvent(new Event('change')); });
    plus  && plus.addEventListener('click', () => { inp.value = (parseInt(inp.value)||1)+1; inp.dispatchEvent(new Event('change')); });
  });

  // === Variant picker (works with both .vp-swatch and legacy .swatch) ===
  const productJson = document.getElementById('ProductJson');
  if(productJson){
    let product = null;
    try { product = JSON.parse(productJson.textContent); } catch(e){ /* invalid JSON */ }
    const form = document.getElementById('ProductForm');
    if(form && product){
      const newGroups = form.querySelectorAll('.vp-group');
      const legacyGroups = form.querySelectorAll('[data-option-input]');
      const groups = newGroups.length ? newGroups : legacyGroups;
      const swatchSel = newGroups.length ? '.vp-swatch' : '.swatch';
      const activeCls = newGroups.length ? 'is-active' : 'active';

      groups.forEach(group => {
        group.querySelectorAll(swatchSel).forEach(sw => sw.addEventListener('click', () => {
          if(sw.getAttribute('aria-disabled') === 'true' && newGroups.length){
            // Sold-out swatch: still allow selection so user sees back-in-stock variant state
          }
          group.querySelectorAll(swatchSel).forEach(s => {
            s.classList.remove(activeCls);
            if(newGroups.length) s.setAttribute('aria-pressed','false');
          });
          sw.classList.add(activeCls);
          if(newGroups.length) sw.setAttribute('aria-pressed','true');
          const labelEl = group.querySelector('[data-option-selected]');
          if(labelEl) labelEl.textContent = sw.dataset.value;
          updateVariant();
        }));
      });

      function updateVariant(){
        const selected = [];
        groups.forEach(g => {
          const a = g.querySelector(swatchSel + '.' + activeCls);
          selected.push(a ? a.dataset.value : null);
        });
        const v = product.variants.find(v => v.options.every((o,i) => o === selected[i]));
        if(!v) return;
        const idIn = form.querySelector('[data-variant-id]') || form.querySelector('[name=id]');
        if(idIn) idIn.value = v.id;

        // Update price (new .price snippet renderer + legacy [data-product-price])
        const priceEl = document.querySelector('.pdp-price .price-current, [data-product-price]');
        if(priceEl && v.price != null) priceEl.textContent = formatMoney(v.price);
        const cmpEl = document.querySelector('.pdp-price .price-compare, [data-product-compare]');
        if(cmpEl){
          if(v.compare_at_price && v.compare_at_price > v.price){
            cmpEl.textContent = formatMoney(v.compare_at_price);
            cmpEl.style.display = '';
          } else {
            cmpEl.style.display = 'none';
          }
        }

        // Update ATC button + sticky price
        const atc = document.querySelector('[data-atc]');
        if(atc){
          atc.disabled = !v.available;
          const atcPriceEl = atc.querySelector('[data-atc-price]');
          if(v.available){
            const lbl = atc.dataset.labelAvailable;
            if(atcPriceEl){
              atc.innerHTML = lbl + ' · <span data-atc-price>' + formatMoney(v.price) + '</span>';
            } else {
              atc.textContent = lbl;
            }
          } else {
            atc.textContent = atc.dataset.labelSoldout;
          }
        }
        const saPrice = document.querySelector('[data-sa-price]');
        if(saPrice && v.price != null) saPrice.textContent = formatMoney(v.price);

        // Swap main image to variant featured image (if available)
        if(v.featured_image && v.featured_image.src){
          const mainImg = document.getElementById('PdpMainImg');
          if(mainImg) mainImg.src = v.featured_image.src.replace(/\.(jpg|jpeg|png|webp)(\?.*)?$/i, '_1200x.$1$2');
        }

        // Update URL with ?variant=
        if(v.id && history.replaceState){
          const u = new URL(window.location);
          u.searchParams.set('variant', v.id);
          history.replaceState({}, '', u.toString());
        }
      }
    }
  }

  // === PDP thumbnail click swap ===
  document.querySelectorAll('.pdp-thumbs .pdp-thumb').forEach(t => {
    t.addEventListener('click', () => {
      document.querySelectorAll('.pdp-thumbs .pdp-thumb').forEach(x => x.classList.remove('active'));
      t.classList.add('active');
      const main = document.getElementById('PdpMainImg');
      const src = t.getAttribute('data-thumb-src') || t.querySelector('img')?.src;
      if(main && src) main.src = src;
    });
  });

  // === Sticky ATC mobile ===
  document.querySelectorAll('[data-sticky-atc]').forEach(b => {
    b.addEventListener('click', () => {
      const form = document.getElementById('ProductForm');
      if(form){
        const submit = form.querySelector('[data-atc]');
        if(submit && !submit.disabled) submit.click();
      }
    });
  });

  // === Recently viewed (localStorage) ===
  (function recentlyViewed(){
    const KEY = 'botteghino_recent';
    const MAX = 8;
    function read(){
      try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch(e){ return []; }
    }
    function write(arr){
      try { localStorage.setItem(KEY, JSON.stringify(arr.slice(0, MAX))); } catch(e){}
    }
    // Save current PDP
    const container = document.getElementById('RecentlyViewed');
    if(container){
      const handle = container.getAttribute('data-current-handle');
      if(handle){
        const list = read().filter(h => h !== handle);
        list.unshift(handle);
        write(list);
        // Render the rest (excluding current)
        const toShow = list.filter(h => h !== handle).slice(0, 4);
        if(toShow.length > 0){
          const grid = container.querySelector('[data-recently-viewed-grid]');
          Promise.all(toShow.map(h =>
            fetch('/products/' + h + '?view=card')
              .then(r => r.ok ? r.text() : '')
              .catch(() => '')
          )).then(htmls => {
            const useable = htmls.filter(Boolean);
            if(useable.length > 0 && grid){
              grid.innerHTML = useable.join('');
              container.hidden = false;
            }
          });
        }
      }
    }
  })();

  // === Back-in-stock form (graceful: contact form submit) ===
  document.querySelectorAll('[data-back-in-stock] form.bis-form').forEach(f => {
    f.addEventListener('submit', () => {
      const btn = f.querySelector('button[type=submit]');
      if(btn){ btn.disabled = true; btn.textContent = '...'; }
    });
  });

  // === Search overlay (Sprint 4) ===
  (function searchOverlay(){
    const overlay = document.getElementById('SearchOverlay');
    if(!overlay) return;
    const input = document.getElementById('SearchInput');
    const results = document.getElementById('SearchResults');
    const empty = document.getElementById('SearchEmpty');
    const loading = document.getElementById('SearchLoading');
    const noResults = document.getElementById('SearchNoResults');
    const noResultsTerm = document.getElementById('SearchNoResultsTerm');
    const productsEl = document.getElementById('SearchProducts');
    const collectionsEl = document.getElementById('SearchCollections');
    const allLink = document.getElementById('SearchAllLink');

    function open(){
      overlay.hidden = false;
      overlay.setAttribute('aria-hidden','false');
      document.body.style.overflow = 'hidden';
      setTimeout(() => input?.focus(), 60);
    }
    function close(){
      overlay.hidden = true;
      overlay.setAttribute('aria-hidden','true');
      document.body.style.overflow = '';
    }

    // Triggers
    document.querySelectorAll('[data-search-open]').forEach(b => b.addEventListener('click', e => { e.preventDefault(); open(); }));
    overlay.querySelectorAll('[data-search-close]').forEach(b => b.addEventListener('click', close));

    // Keyboard shortcuts
    document.addEventListener('keydown', e => {
      if(e.key === 'Escape' && !overlay.hidden){ close(); return; }
      if(overlay.hidden){
        if((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k'){ e.preventDefault(); open(); }
        else if(e.key === '/' && !['INPUT','TEXTAREA','SELECT'].includes(e.target.tagName)){ e.preventDefault(); open(); }
      }
    });

    // Predictive search (debounced)
    let timer = null;
    let abortCtl = null;
    input?.addEventListener('input', () => {
      clearTimeout(timer);
      const q = input.value.trim();
      if(q.length < 2){
        results.hidden = true; noResults.hidden = true; empty.hidden = false; loading.hidden = true;
        return;
      }
      timer = setTimeout(() => doSearch(q), 250);
    });

    async function doSearch(q){
      empty.hidden = true; noResults.hidden = true; loading.hidden = false; results.hidden = true;
      if(abortCtl) abortCtl.abort();
      abortCtl = new AbortController();
      try {
        const url = '/search/suggest.json?q=' + encodeURIComponent(q) +
          '&resources[type]=product,collection&resources[limit]=6&resources[options][unavailable_products]=last';
        const r = await fetch(url, { signal: abortCtl.signal, headers: { Accept: 'application/json' } });
        if(!r.ok) throw new Error('Search failed');
        const data = await r.json();
        const products = data?.resources?.results?.products || [];
        const collections = data?.resources?.results?.collections || [];
        loading.hidden = true;
        if(products.length === 0 && collections.length === 0){
          noResultsTerm.textContent = q;
          noResults.hidden = false;
          return;
        }
        productsEl.innerHTML = products.map(p => productHtml(p)).join('') || '<p class="muted">Nessun prodotto.</p>';
        collectionsEl.innerHTML = collections.map(c => '<li><a href="' + c.url + '">' + escapeHtml(c.title) + '</a></li>').join('') || '<li class="muted">Nessuna collezione.</li>';
        allLink.href = '/search?q=' + encodeURIComponent(q);
        allLink.hidden = false;
        results.hidden = false;
      } catch(err){
        if(err.name === 'AbortError') return;
        loading.hidden = true;
        empty.hidden = false;
      }
    }
    function productHtml(p){
      const img = p.image ? '<img src="' + p.image + '" alt="' + escapeHtml(p.title) + '" loading="lazy">' : '';
      return '<a href="' + p.url + '" class="search-result"><div class="search-result-img">' + img + '</div>' +
        '<div class="search-result-info"><span class="search-result-brand">' + escapeHtml(p.vendor || '') + '</span>' +
        '<span class="search-result-name">' + escapeHtml(p.title) + '</span>' +
        '<span class="search-result-price">' + p.price + '</span></div></a>';
    }
    function escapeHtml(s){ return String(s).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c])); }
  })();

  // === Quick view (Sprint 4) ===
  (function quickView(){
    const modal = document.getElementById('QuickViewModal');
    if(!modal) return;
    const body = document.getElementById('QuickViewBody');

    async function open(handle){
      modal.hidden = false;
      modal.setAttribute('aria-hidden','false');
      document.body.style.overflow = 'hidden';
      body.setAttribute('aria-busy','true');
      body.innerHTML = '<div class="qv-loading">Caricamento…</div>';
      try {
        const r = await fetch('/products/' + handle + '?view=quick-view', { headers: { Accept: 'text/html' } });
        if(!r.ok) throw new Error('Quick view fetch failed');
        const html = await r.text();
        body.innerHTML = html;
        body.setAttribute('aria-busy','false');
        // Re-bind: variant picker, qty +/-, atc, thumbs
        bindQuickViewHandlers(body);
      } catch(e){
        body.innerHTML = '<p class="muted" style="text-align:center;padding:32px">Impossibile caricare la vista rapida. <a href="/products/' + handle + '">Apri scheda completa →</a></p>';
        body.setAttribute('aria-busy','false');
      }
    }
    function close(){
      modal.hidden = true;
      modal.setAttribute('aria-hidden','true');
      document.body.style.overflow = '';
      body.innerHTML = '<div class="qv-loading">Caricamento…</div>';
    }
    function bindQuickViewHandlers(root){
      // qty +/-
      root.querySelectorAll('.qty').forEach(q => {
        const inp = q.querySelector('input');
        q.querySelector('[data-qty-minus]')?.addEventListener('click', () => { inp.value = Math.max(1,(parseInt(inp.value)||1)-1); });
        q.querySelector('[data-qty-plus]')?.addEventListener('click', () => { inp.value = (parseInt(inp.value)||1)+1; });
      });
      // thumbs
      root.querySelectorAll('.qv-thumb').forEach(t => t.addEventListener('click', () => {
        root.querySelectorAll('.qv-thumb').forEach(x => x.classList.remove('is-active'));
        t.classList.add('is-active');
        const main = root.querySelector('#QvMainImg');
        const src = t.getAttribute('data-thumb-src');
        if(main && src) main.src = src;
      }));
      // variant picker (re-use new vp-* logic inline)
      const json = root.querySelector('#ProductJson');
      if(!json) return;
      let product = null;
      try { product = JSON.parse(json.textContent); } catch(e){ return; }
      const form = root.querySelector('[data-qv-form]');
      if(!form) return;
      const groups = form.querySelectorAll('.vp-group');
      groups.forEach(g => {
        g.querySelectorAll('.vp-swatch').forEach(sw => sw.addEventListener('click', () => {
          g.querySelectorAll('.vp-swatch').forEach(s => { s.classList.remove('is-active'); s.setAttribute('aria-pressed','false'); });
          sw.classList.add('is-active'); sw.setAttribute('aria-pressed','true');
          const lbl = g.querySelector('[data-option-selected]');
          if(lbl) lbl.textContent = sw.dataset.value;
          updateVariant();
        }));
      });
      function updateVariant(){
        const selected = [];
        groups.forEach(g => {
          const a = g.querySelector('.vp-swatch.is-active');
          selected.push(a ? a.dataset.value : null);
        });
        const v = product.variants.find(v => v.options.every((o,i) => o === selected[i]));
        if(!v) return;
        const idIn = form.querySelector('[data-variant-id]');
        if(idIn) idIn.value = v.id;
        const atc = form.querySelector('[data-atc]');
        if(atc){
          atc.disabled = !v.available;
          if(v.available){
            atc.innerHTML = atc.dataset.labelAvailable + ' · <span data-atc-price>' + formatMoney(v.price) + '</span>';
          } else {
            atc.textContent = atc.dataset.labelSoldout;
          }
        }
        if(v.featured_image?.src){
          const main = root.querySelector('#QvMainImg');
          if(main) main.src = v.featured_image.src;
        }
      }
      // ATC AJAX (re-use submit handler from global)
      form.addEventListener('submit', async e => {
        e.preventDefault();
        const fd = new FormData(form);
        const btn = form.querySelector('[data-atc]');
        const orig = btn?.textContent;
        if(btn){ btn.disabled = true; btn.textContent = '...'; }
        try {
          const r = await fetch('/cart/add.js', { method: 'POST', body: fd, headers: { Accept: 'application/json' } });
          if(!r.ok) throw new Error('ATC failed');
          await r.json();
          if(btn){ btn.textContent = '✓ Aggiunto'; }
          setTimeout(() => {
            close();
            const drawer = document.getElementById('cart-drawer');
            if(drawer){ drawer.classList.add('open'); drawer.setAttribute('aria-hidden','false'); }
          }, 700);
        } catch(err){
          if(btn) btn.textContent = orig || 'Errore';
        } finally {
          if(btn) btn.disabled = false;
        }
      });
    }

    // Open triggers (delegation for product cards + lookbook hotspots)
    document.addEventListener('click', e => {
      const t = e.target.closest('[data-quick-view]');
      if(!t) return;
      e.preventDefault();
      const handle = t.getAttribute('data-quick-view');
      if(handle) open(handle);
    });
    // Close
    modal.querySelectorAll('[data-qv-close]').forEach(b => b.addEventListener('click', close));
    document.addEventListener('keydown', e => { if(e.key === 'Escape' && !modal.hidden) close(); });
  })();

  // === Account: Address toggle (edit / new) + delete confirm ===
  document.querySelectorAll('[data-addr-toggle]').forEach(b => {
    b.addEventListener('click', e => {
      e.preventDefault();
      const id = b.getAttribute('data-addr-toggle');
      const form = document.querySelector('[data-addr-form="' + id + '"]');
      if(!form) return;
      const card = b.closest('[data-addr-card]');
      const view = card ? card.querySelector('.acc-address-view') : null;
      const isOpen = !form.hidden;
      if(isOpen){
        form.hidden = true;
        if(view) view.hidden = false;
      } else {
        // close any other open forms
        document.querySelectorAll('[data-addr-form]').forEach(f => { f.hidden = true; });
        document.querySelectorAll('.acc-address-view').forEach(v => { v.hidden = false; });
        form.hidden = false;
        if(view) view.hidden = true;
        form.querySelector('input, select, textarea')?.focus();
      }
    });
  });
  document.querySelectorAll('[data-addr-delete]').forEach(b => {
    b.addEventListener('click', e => {
      e.preventDefault();
      if(!confirm('Eliminare questo indirizzo? L\'operazione è definitiva.')) return;
      const fid = b.getAttribute('data-form-id');
      const f = document.getElementById(fid);
      if(f) f.submit();
    });
  });

  // === Collection filters ===
  (function collectionFilters(){
    const form = document.getElementById('CollectionFiltersForm');
    if(!form) return;
    const filters = document.getElementById('CollectionFilters');
    const backdrop = document.querySelector('.filters-backdrop');
    const sortSelect = document.querySelector('[data-sort-select]');

    // Mobile open/close
    document.querySelectorAll('[data-filters-open]').forEach(b =>
      b.addEventListener('click', () => {
        filters?.classList.add('is-open');
        backdrop?.removeAttribute('hidden');
        document.body.style.overflow = 'hidden';
      })
    );
    document.querySelectorAll('[data-filters-close]').forEach(b =>
      b.addEventListener('click', () => {
        filters?.classList.remove('is-open');
        backdrop?.setAttribute('hidden','');
        document.body.style.overflow = '';
      })
    );

    // Auto-submit on change (desktop), but debounced for price inputs
    let priceTimer;
    form.addEventListener('change', e => {
      if(e.target.type === 'number') return;
      form.submit();
    });
    form.querySelectorAll('input[type=number]').forEach(inp =>
      inp.addEventListener('input', () => {
        clearTimeout(priceTimer);
        priceTimer = setTimeout(() => form.submit(), 700);
      })
    );

    // Sort triggers a URL change (preserve filters)
    if(sortSelect){
      sortSelect.addEventListener('change', () => {
        const u = new URL(window.location);
        u.searchParams.set('sort_by', sortSelect.value);
        window.location = u.toString();
      });
    }
  })();

  function formatMoney(cents){
    return '€' + (cents/100).toFixed(2).replace('.',',');
  }

  // === AJAX add to cart ===
  document.querySelectorAll('form[action*="/cart/add"]').forEach(f => {
    f.addEventListener('submit', async e => {
      e.preventDefault();
      const fd = new FormData(f);
      const btn = f.querySelector('[type=submit]');
      const origLabel = btn ? btn.textContent : '';
      if(btn){ btn.disabled = true; btn.textContent = '...'; }
      try {
        const r = await fetch('/cart/add.js', { method:'POST', body:fd, headers:{Accept:'application/json'} });
        if(!r.ok) throw new Error('ATC failed');
        await r.json();
        updateCartCount();
        const drawer = document.getElementById('cart-drawer');
        if(drawer){ drawer.classList.add('open'); refreshDrawer(); }
        if(btn){ btn.textContent = '✓ Aggiunto'; setTimeout(() => btn.textContent = origLabel, 1500); }
      } catch(err){
        if(btn) btn.textContent = origLabel;
        alert('Errore: ' + err.message);
      } finally {
        if(btn) btn.disabled = false;
      }
    });
  });

  async function updateCartCount(){
    try {
      const r = await fetch('/cart.js'); const c = await r.json();
      document.querySelectorAll('.cart-count').forEach(el => el.textContent = c.item_count);
    } catch(e){ /* fail silently */ }
  }
  async function refreshDrawer(){
    try {
      const r = await fetch('/?section_id=cart-drawer-content');
      const t = await r.text();
      const body = document.getElementById('cart-drawer-body');
      if(body) body.innerHTML = new DOMParser().parseFromString(t,'text/html').querySelector('.shopify-section')?.innerHTML || t;
    } catch(e){ /* fail silently */ }
  }

  // === Cart drawer qty + remove ===
  document.addEventListener('click', async e => {
    // Qty +/-
    const qtyBtn = e.target.closest('[data-cart-qty]');
    if(qtyBtn){
      e.preventDefault();
      const delta = parseInt(qtyBtn.getAttribute('data-cart-qty'), 10);
      const line  = parseInt(qtyBtn.getAttribute('data-line'), 10);
      const cur   = qtyBtn.parentElement.querySelector('.cdi-qty-val');
      const next  = Math.max(0, (parseInt(cur?.textContent || '1', 10)) + delta);
      await updateLine(line, next);
      return;
    }
    // Remove
    const rmBtn = e.target.closest('[data-cart-remove]');
    if(rmBtn){
      e.preventDefault();
      const line = parseInt(rmBtn.getAttribute('data-line'), 10);
      await updateLine(line, 0);
      return;
    }
  });

  async function updateLine(line, quantity){
    try {
      const r = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
      if(!r.ok) throw new Error('Cart update failed');
      await r.json();
      updateCartCount();
      refreshDrawer();
      if(quantity === 0 && window.Botteghino && window.Botteghino.toast){
        window.Botteghino.toast.info('Articolo rimosso dal carrello');
      }
    } catch(err){
      if(window.Botteghino && window.Botteghino.toast){
        window.Botteghino.toast.danger('Errore aggiornamento carrello');
      } else {
        alert('Errore: ' + err.message);
      }
    }
  }

  // Initial cart count
  if(document.querySelector('.cart-count')) updateCartCount();

  // === Filter toggle mobile ===
  const filterBtn = document.querySelector('.mobile-filter-btn');
  const filtersEl = document.querySelector('.filters');
  if(filterBtn && filtersEl){
    filterBtn.addEventListener('click', () => filtersEl.classList.toggle('open'));
  }

})();
